/**
 * The Listing's terms form, in one place: rent and availability, lease terms, move-in costs, utilities
 * and insurance, pets, house rules and income limits. Used by the guide's Listing details step (create
 * on first save, then update) and by the live Listing editor (update), so the ~30 fields, their
 * validation, dirty tracking and request-body building exist once.
 *
 * Jurisdiction warnings are soft (BR-1a): nothing here blocks a save on them. BR-1b shape errors are
 * rejected by the server and surfaced by the caller.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  type CreateListingParams,
  type UpdateListingParams,
  type FurnishedOption,
  type LeaseTermOption,
  type Listing,
  type ListingConstraints,
  type ListingIncomeRestrictions,
  type MaxPetSize,
  type PetType,
  type UtilityType,
} from '../../../lib/leasing.api';

export const INCOME_SIZES = [
  { key: 'one', field: 'onePersonCents', label: '1 person', testid: 'income-one-person' },
  { key: 'two', field: 'twoPersonCents', label: '2 people', testid: 'income-two-person' },
  { key: 'three', field: 'threePersonCents', label: '3 people', testid: 'income-three-person' },
  { key: 'four', field: 'fourPersonCents', label: '4 people', testid: 'income-four-person' },
  { key: 'five', field: 'fivePersonCents', label: '5 people', testid: 'income-five-person' },
  { key: 'six', field: 'sixPersonCents', label: '6 people', testid: 'income-six-person' },
  { key: 'seven', field: 'sevenPersonCents', label: '7 people', testid: 'income-seven-person' },
  { key: 'eight', field: 'eightPersonCents', label: '8 people', testid: 'income-eight-person' },
] as const satisfies readonly { key: string; field: keyof ListingIncomeRestrictions; label: string; testid: string }[];

export type IncomeSizeKey = (typeof INCOME_SIZES)[number]['key'];

export interface ListingTermsValues {
  askingRent: string;
  availableDate: string;
  acceptedLeaseTerms: LeaseTermOption[];
  smokingAllowed: boolean;
  securityDeposit: string;
  applicationFee: string;
  adminFee: string;
  holdingDeposit: string;
  parkingFee: string;
  firstMonthRequired: boolean;
  lastMonthRequired: boolean;
  petsAllowed: boolean;
  petTypesAllowed: PetType[];
  maxPetSize: MaxPetSize | '';
  petDeposit: string;
  petRent: string;
  /** Minimum monthly household income per household size, in dollars; blank means none. */
  income: Record<IncomeSizeKey, string>;
  utilitiesIncluded: UtilityType[];
  furnished: FurnishedOption;
  requireRentersInsurance: boolean;
}

const BLANK_INCOME: Record<IncomeSizeKey, string> = {
  one: '',
  two: '',
  three: '',
  four: '',
  five: '',
  six: '',
  seven: '',
  eight: '',
};

/** Values for a Listing that does not exist yet (the guide's first save creates it). */
export const NEW_LISTING_TERMS: ListingTermsValues = {
  askingRent: '',
  availableDate: '',
  acceptedLeaseTerms: ['12_months'],
  smokingAllowed: false,
  securityDeposit: '',
  applicationFee: '',
  adminFee: '',
  holdingDeposit: '',
  parkingFee: '',
  firstMonthRequired: true,
  lastMonthRequired: false,
  petsAllowed: false,
  petTypesAllowed: [],
  maxPetSize: '',
  petDeposit: '',
  petRent: '',
  income: BLANK_INCOME,
  utilitiesIncluded: [],
  furnished: 'unfurnished',
  requireRentersInsurance: false,
};

const dollars = (cents: number | null | undefined): string => (cents != null ? String(cents / 100) : '');

export function termsValuesFromListing(listing: Listing): ListingTermsValues {
  const fees = listing.fees ?? {};
  const pets = listing.petPolicy ?? null;
  const income = listing.incomeRestrictions ?? {};
  return {
    askingRent: dollars(listing.askingRentCents),
    availableDate: listing.availableDate.slice(0, 10),
    acceptedLeaseTerms: listing.acceptedLeaseTerms,
    smokingAllowed: listing.smokingAllowed,
    securityDeposit: dollars(fees.securityDepositCents),
    applicationFee: dollars(fees.applicationFeeCents),
    adminFee: dollars(fees.administrationFeeCents),
    holdingDeposit: dollars(fees.holdingDepositCents),
    parkingFee: dollars(fees.parkingFeeMonthlyCents),
    firstMonthRequired: fees.firstMonthRequired ?? false,
    lastMonthRequired: fees.lastMonthRequired ?? false,
    petsAllowed: pets?.petsAllowed ?? false,
    petTypesAllowed: pets?.petTypesAllowed ?? [],
    maxPetSize: pets?.maxPetSize ?? '',
    petDeposit: dollars(fees.petDepositCents),
    petRent: dollars(fees.petRentMonthlyCents),
    income: Object.fromEntries(
      INCOME_SIZES.map(({ key, field }) => [key, dollars(income[field])])
    ) as Record<IncomeSizeKey, string>,
    utilitiesIncluded: listing.utilitiesIncluded ?? [],
    furnished: listing.furnished ?? 'unfurnished',
    requireRentersInsurance: listing.requireRentersInsurance ?? false,
  };
}

/** Dollars typed in a field -> whole cents; blank or unparseable -> null. */
export function toCents(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : Math.round(parsed * 100);
}

/** What a tenant pays to move in: first/last month, deposits and the application fee. */
export function estimateMoveInTotalCents(values: ListingTermsValues): number {
  const rent = toCents(values.askingRent) ?? 0;
  const petDeposit = values.petsAllowed ? (toCents(values.petDeposit) ?? 0) : 0;
  return (
    (values.firstMonthRequired ? rent : 0) +
    (values.lastMonthRequired ? rent : 0) +
    (toCents(values.securityDeposit) ?? 0) +
    petDeposit +
    (toCents(values.applicationFee) ?? 0) +
    (toCents(values.holdingDeposit) ?? 0) +
    (toCents(values.adminFee) ?? 0)
  );
}

/**
 * The amount a jurisdiction's move-in cap applies to (security deposit, last month's rent, and the pet
 * deposit where the jurisdiction counts it) against the cap itself; null when it has no such cap.
 */
export function legalMoveInAgainstCap(
  values: ListingTermsValues,
  constraints: ListingConstraints | null
): { totalCents: number; capCents: number } | null {
  const multiplier = constraints?.moveInCost.maxFirstMonthMultiplier;
  const rent = toCents(values.askingRent);
  if (!constraints || multiplier == null || rent == null) return null;
  const petDeposit =
    constraints.moveInCost.petDepositCountedInCeiling && values.petsAllowed
      ? (toCents(values.petDeposit) ?? 0)
      : 0;
  const totalCents =
    (toCents(values.securityDeposit) ?? 0) + (values.lastMonthRequired ? rent : 0) + petDeposit;
  return { totalCents, capCents: Math.round(multiplier * rent) };
}

/** Number of soft jurisdiction warnings the current values trigger (for a header note); BR-1a: never blocks. */
export function countTermsWarnings(values: ListingTermsValues, constraints: ListingConstraints | null): number {
  const rent = toCents(values.askingRent);
  let count = 0;
  const depositMultiplier = constraints?.securityDeposit.maxMultiplier;
  const deposit = toCents(values.securityDeposit);
  if (rent != null && depositMultiplier != null && deposit != null && deposit > depositMultiplier * rent) count += 1;
  const legal = legalMoveInAgainstCap(values, constraints);
  if (legal && legal.totalCents > legal.capCents) count += 1;
  return count;
}

const MONEY_FIELDS = [
  ['securityDeposit', 'Security deposit'],
  ['applicationFee', 'Application fee'],
  ['adminFee', 'Administration fee'],
  ['holdingDeposit', 'Holding deposit'],
  ['parkingFee', 'Parking fee'],
  ['petDeposit', 'Pet deposit'],
  ['petRent', 'Pet rent'],
] as const satisfies readonly (readonly [keyof ListingTermsValues, string])[];

export type TermsErrorKey = keyof ListingTermsValues | `income.${IncomeSizeKey}`;
export type ListingTermsErrors = Partial<Record<TermsErrorKey, string>>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function validateListingTerms(values: ListingTermsValues): ListingTermsErrors {
  const errors: ListingTermsErrors = {};
  const rent = toCents(values.askingRent);
  if (rent == null || rent <= 0) errors.askingRent = 'Enter an asking rent above $0.';
  if (!ISO_DATE.test(values.availableDate) || Number.isNaN(Date.parse(values.availableDate))) {
    errors.availableDate = 'Enter the date it is available from.';
  }
  if (values.acceptedLeaseTerms.length === 0) errors.acceptedLeaseTerms = 'Choose at least one lease term.';
  for (const [field, label] of MONEY_FIELDS) {
    const cents = toCents(String(values[field]));
    if (cents != null && cents < 0) errors[field] = `${label} cannot be negative.`;
  }
  for (const { key, label } of INCOME_SIZES) {
    const cents = toCents(values.income[key]);
    if (cents != null && cents < 0) errors[`income.${key}`] = `${label} cannot be negative.`;
  }
  return errors;
}

function feesBody(values: ListingTermsValues) {
  return {
    securityDepositCents: toCents(values.securityDeposit),
    applicationFeeCents: toCents(values.applicationFee),
    petDepositCents: values.petsAllowed ? toCents(values.petDeposit) : null,
    petRentMonthlyCents: values.petsAllowed ? toCents(values.petRent) : null,
    firstMonthRequired: values.firstMonthRequired,
    lastMonthRequired: values.lastMonthRequired,
    administrationFeeCents: toCents(values.adminFee),
    holdingDepositCents: toCents(values.holdingDeposit),
    parkingFeeMonthlyCents: toCents(values.parkingFee),
  };
}

/** Income limits are sent once any size is filled, or to clear limits a saved Listing already has. */
function incomeBody(values: ListingTermsValues, hadIncomeRestrictions: boolean): ListingIncomeRestrictions | undefined {
  const filled = INCOME_SIZES.some(({ key }) => toCents(values.income[key]) != null);
  if (!filled && !hadIncomeRestrictions) return undefined;
  return Object.fromEntries(
    INCOME_SIZES.map(({ key, field }) => [field, toCents(values.income[key])])
  ) as ListingIncomeRestrictions;
}

export function buildCreateListingBody(values: ListingTermsValues, unitId: string): CreateListingParams {
  const incomeRestrictions = incomeBody(values, false);
  return {
    unitId,
    askingRentCents: toCents(values.askingRent) ?? 0,
    availableDate: values.availableDate,
    acceptedLeaseTerms: values.acceptedLeaseTerms,
    smokingAllowed: values.smokingAllowed,
    fees: feesBody(values),
    petPolicy: {
      petsAllowed: values.petsAllowed,
      ...(values.petsAllowed && values.petTypesAllowed.length > 0 ? { petTypesAllowed: values.petTypesAllowed } : {}),
      ...(values.petsAllowed && values.maxPetSize ? { maxPetSize: values.maxPetSize } : {}),
    },
    ...(incomeRestrictions ? { incomeRestrictions } : {}),
    utilitiesIncluded: values.utilitiesIncluded,
    furnished: values.furnished,
    requireRentersInsurance: values.requireRentersInsurance,
  };
}

export function buildUpdateListingBody(
  values: ListingTermsValues,
  hadIncomeRestrictions: boolean
): UpdateListingParams {
  return {
    askingRentCents: toCents(values.askingRent) ?? 0,
    availableDate: values.availableDate,
    acceptedLeaseTerms: values.acceptedLeaseTerms,
    smokingAllowed: values.smokingAllowed,
    fees: feesBody(values),
    petPolicy: {
      petsAllowed: values.petsAllowed,
      petTypesAllowed: values.petTypesAllowed,
      maxPetSize: values.maxPetSize || null,
    },
    incomeRestrictions: incomeBody(values, hadIncomeRestrictions),
    utilitiesIncluded: values.utilitiesIncluded,
    furnished: values.furnished,
    requireRentersInsurance: values.requireRentersInsurance,
  };
}

function toggled<T>(list: readonly T[], item: T): T[] {
  return list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];
}

export interface ListingTermsForm {
  values: ListingTermsValues;
  errors: ListingTermsErrors;
  dirty: boolean;
  /** Estimated tenant move-in total, in cents. */
  moveInTotalCents: number;
  set: <K extends keyof ListingTermsValues>(key: K, value: ListingTermsValues[K]) => void;
  setIncome: (size: IncomeSizeKey, value: string) => void;
  toggleLeaseTerm: (term: LeaseTermOption) => void;
  toggleUtility: (util: UtilityType) => void;
  togglePetType: (species: PetType) => void;
  /** Validate and show inline errors; true when the terms can be sent. */
  validate: () => boolean;
  toCreateBody: (unitId: string) => CreateListingParams;
  toUpdateBody: () => UpdateListingParams;
  /** Treat the current values as saved (clears the dirty flag). */
  markSaved: () => void;
  /** Throw away edits back to the last saved values. */
  reset: () => void;
}

export function useListingTermsForm(
  initial: ListingTermsValues,
  options: { hadIncomeRestrictions?: boolean } = {}
): ListingTermsForm {
  const [values, setValues] = useState<ListingTermsValues>(initial);
  const [saved, setSaved] = useState<ListingTermsValues>(initial);
  const [errors, setErrors] = useState<ListingTermsErrors>({});
  const [hadIncome, setHadIncome] = useState(options.hadIncomeRestrictions ?? false);

  const clearError = useCallback((key: TermsErrorKey) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      void _removed;
      return rest;
    });
  }, []);

  const set = useCallback(
    <K extends keyof ListingTermsValues>(key: K, value: ListingTermsValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      clearError(key);
    },
    [clearError]
  );

  const setIncome = useCallback(
    (size: IncomeSizeKey, value: string) => {
      setValues((prev) => ({ ...prev, income: { ...prev.income, [size]: value } }));
      clearError(`income.${size}`);
    },
    [clearError]
  );

  const toggleLeaseTerm = useCallback(
    (term: LeaseTermOption) => {
      setValues((prev) => ({ ...prev, acceptedLeaseTerms: toggled(prev.acceptedLeaseTerms, term) }));
      clearError('acceptedLeaseTerms');
    },
    [clearError]
  );
  const toggleUtility = useCallback((util: UtilityType) => {
    setValues((prev) => ({ ...prev, utilitiesIncluded: toggled(prev.utilitiesIncluded, util) }));
  }, []);
  const togglePetType = useCallback((species: PetType) => {
    setValues((prev) => ({ ...prev, petTypesAllowed: toggled(prev.petTypesAllowed, species) }));
  }, []);

  const validate = useCallback(() => {
    const next = validateListingTerms(values);
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [values]);

  const markSaved = useCallback(() => {
    setSaved(values);
    setHadIncome(INCOME_SIZES.some(({ key }) => toCents(values.income[key]) != null));
  }, [values]);

  const reset = useCallback(() => {
    setValues(saved);
    setErrors({});
  }, [saved]);

  const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(saved), [values, saved]);
  const moveInTotalCents = useMemo(() => estimateMoveInTotalCents(values), [values]);

  return {
    values,
    errors,
    dirty,
    moveInTotalCents,
    set,
    setIncome,
    toggleLeaseTerm,
    toggleUtility,
    togglePetType,
    validate,
    toCreateBody: (unitId) => buildCreateListingBody(values, unitId),
    toUpdateBody: () => buildUpdateListingBody(values, hadIncome),
    markSaved,
    reset,
  };
}
